import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { getDb, initFirebase } from '../firebase';

const COLLECTIONS = ['movimientos','compras','costosMes','semanas','socios','inversiones','cuentasBancarias'];

function ls(k) {
  try { const v = localStorage.getItem('farm2-' + k); return v ? JSON.parse(v) : null; } catch { return null; }
}
function lsSave(k, v) {
  try { localStorage.setItem('farm2-' + k, JSON.stringify(v)); } catch {}
}

const EMPTY = { movimientos:{}, compras:{}, costosMes:{}, semanas:{}, socios:{}, users:[], inversiones:{} };

export function useStore() {
  const [store, setStore] = useState(() => ({
    ...EMPTY,
    movimientos: ls('movimientos') || {},
    compras: ls('compras') || {},
    costosMes: ls('costosMes') || {},
    semanas: ls('semanas') || {},
    socios: ls('socios') || {},
    users: ls('users') || [],
    inversiones: ls('inversiones') || {},
    cuentasBancarias: ls('cuentasBancarias') || {},
    connected: false,
    loading: true,
  }));

  const dbRef = useRef(null);
  const subsRef = useRef([]);

  function subscribeAll(db) {
    if (dbRef.current) return;
    dbRef.current = db;

    COLLECTIONS.forEach(k => {
      const r = ref(db, k);
      const unsub = onValue(r, snap => {
        const val = snap.val() || {};
        lsSave(k, val);
        setStore(prev => ({ ...prev, [k]: val, connected: true, loading: false }));
      }, err => {
        console.error('Firebase error for', k, err);
        setStore(prev => ({ ...prev, loading: false }));
      });
      subsRef.current.push(unsub);
    });

    const userRef = ref(db, 'users');
    const userUnsub = onValue(userRef, snap => {
      const raw = snap.val();
      let val = [];
      if (Array.isArray(raw)) val = raw.filter(Boolean);
      else if (raw && typeof raw === 'object') val = Object.values(raw).filter(Boolean);
      lsSave('users', val);
      setStore(prev => ({ ...prev, users: val, connected: true }));
    });
    subsRef.current.push(userUnsub);
  }

  useEffect(() => {
    let db = getDb();
    if (db) {
      subscribeAll(db);
      return () => subsRef.current.forEach(u => u && u());
    }
    // Reintentar cada 200ms hasta que Firebase esté listo (máx 5s)
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      db = getDb();
      if (db) {
        clearInterval(interval);
        subscribeAll(db);
      } else if (attempts > 25) {
        clearInterval(interval);
        setStore(prev => ({ ...prev, loading: false }));
      }
    }, 200);
    return () => {
      clearInterval(interval);
      subsRef.current.forEach(u => u && u());
    };
  }, []);

  const setCol = useCallback((col, data) => {
    lsSave(col, data);
    setStore(prev => ({ ...prev, [col]: data }));
    const db = getDb();
    if (db) {
      const clean = JSON.parse(JSON.stringify(data, (k, v) => v === undefined ? null : v));
      set(ref(db, col), clean).catch(err => console.error('Firebase write error:', err));
    }
  }, []);

  const setItem = useCallback((col, id, data) => {
    setStore(prev => {
      const updated = col === 'users'
        ? (Array.isArray(prev[col]) ? prev[col].map(u => u.id === id ? data : u) : prev[col])
        : { ...prev[col], [id]: data };
      lsSave(col, updated);
      const db = getDb();
      if (db) {
        const clean = JSON.parse(JSON.stringify(updated, (k, v) => v === undefined ? null : v));
        set(ref(db, col), clean).catch(err => console.error('Firebase write error:', err));
      }
      return { ...prev, [col]: updated };
    });
  }, []);

  const delItem = useCallback((col, id) => {
    setStore(prev => {
      const updated = { ...prev[col] };
      delete updated[id];
      lsSave(col, updated);
      const db = getDb();
      if (db) {
        set(ref(db, col), updated).catch(err => console.error('Firebase write error:', err));
      }
      return { ...prev, [col]: updated };
    });
  }, []);

  return { store, setCol, setItem, delItem };
}
